/**
 * Chutt... - Report Editor
 * Allows RP to edit sections, toggle visibility, add comments,
 * and generate a branded PDF report.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Alert,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import SaveIcon from '@mui/icons-material/Save';

import { reportsAPI } from '../../services/api';
import { colors } from '../../styles/colors';

function ReportEditor({ open, onClose, brandId, brandName, agencyLogoUrl, reportId }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [clientName, setClientName] = useState('');
  const [comments, setComments] = useState('');
  const [, setGeneratedAt] = useState(''); // eslint-disable-line no-unused-vars

  // Save edited sections to backend
  const saveSections = useCallback(async (sectionsToSave, commentsToSave) => {
    if (!reportId) return;
    try {
      await reportsAPI.saveSections(reportId, sectionsToSave, commentsToSave || null);
    } catch (err) {
      console.warn('Auto-save sections failed:', err);
    }
  }, [reportId]);

  // Load preview (canonical rich sections), then overlay any saved text edits
  const loadPreview = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      // Always load canonical sections from preview (matches rich PDF structure)
      const res = await reportsAPI.getPreview(brandId);
      const data = res.data || res;
      let previewSections = data.sections || [];
      setGeneratedAt(data.generated_at || '');

      // If saved edits exist, overlay text content + visibility onto preview sections
      if (reportId) {
        try {
          const reportRes = await reportsAPI.getById(reportId);
          const reportData = reportRes.data?.report || reportRes.data || reportRes;
          let saved = reportData.edited_sections;
          if (saved && typeof saved === 'string') {
            try { saved = JSON.parse(saved); } catch (_) { saved = null; }
          }
          if (Array.isArray(saved) && saved.length > 0) {
            const savedMap = {};
            saved.forEach(s => { if (s.id) savedMap[s.id] = s; });
            previewSections = previewSections.map(section => {
              const override = savedMap[section.id];
              if (override) {
                return {
                  ...section,
                  content: override.content !== undefined ? override.content : section.content,
                  visible: override.visible !== undefined ? override.visible : section.visible,
                };
              }
              return section;
            });
          }
          setComments(reportData.editor_comments || '');
        } catch (_) {
          // Ignore — use preview sections as-is
        }
      }

      setSections(previewSections);
    } catch (err) {
      console.error('Report preview error:', err);
      setError('Erreur lors du chargement de l\'apercu du rapport.');
    } finally {
      setLoading(false);
    }
  }, [brandId, reportId]);

  useEffect(() => {
    if (open && brandId) {
      loadPreview();
    }
  }, [open, brandId, loadPreview]);

  // Toggle section visibility
  const handleToggleVisible = (sectionId) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId ? { ...s, visible: !s.visible } : s
      )
    );
  };

  // Update section content
  const handleContentChange = (sectionId, newContent) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId ? { ...s, content: newContent } : s
      )
    );
  };

  // Auto-save on close
  const handleClose = useCallback(() => {
    if (sections.length > 0 && reportId) {
      saveSections(sections, comments);
    }
    onClose();
  }, [sections, comments, reportId, saveSections, onClose]);

  // Save sections + comments to DB, then close
  const handleSave = async () => {
    setGenerating(true);
    setError(null);
    try {
      await saveSections(sections, comments);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      setError('Erreur lors de l\'enregistrement.');
    } finally {
      setGenerating(false);
    }
  };

  const visibleCount = sections.filter((s) => s.visible).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          bgcolor: colors.ivory,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 500,
              fontSize: '1.4rem',
              color: colors.charcoal,
            }}
          >
            Rapport de Reputation
          </Typography>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.82rem',
              color: colors.textMed,
            }}
          >
            {brandName} - Modifiez les sections puis enregistrez
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Fermer">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: colors.ivory }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <>
            {/* Client name + comments */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <TextField
                label="Nom du client (optionnel)"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                size="small"
                fullWidth
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Sections */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <Box
                    key={section.id}
                    sx={{
                      border: `1px solid ${section.visible ? colors.borderLight : colors.gray300}`,
                      borderRadius: '16px',
                      p: 2.5,
                      bgcolor: section.visible
                        ? 'rgba(255,255,255,0.65)'
                        : 'rgba(155,168,162,0.08)',
                      opacity: section.visible ? 1 : 0.6,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: section.visible ? 1.5 : 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: '"Playfair Display", Georgia, serif',
                            fontWeight: 500,
                            fontSize: '1rem',
                            color: section.visible
                              ? colors.charcoal
                              : colors.textLight,
                          }}
                        >
                          {section.title}
                        </Typography>
                        {section.editable && section.visible && (
                          <Chip
                            label="Editable"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: `${colors.primary}14`,
                              color: colors.primary,
                              borderRadius: '9999px',
                            }}
                          />
                        )}
                        {!section.editable && section.visible && (
                          <Chip
                            label="Donnees"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: `${colors.info}14`,
                              color: colors.info,
                              borderRadius: '9999px',
                            }}
                          />
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        aria-label={section.visible ? 'Masquer la section' : 'Afficher la section'}
                        onClick={() => handleToggleVisible(section.id)}
                        sx={{ color: section.visible ? colors.primary : colors.textLight }}
                      >
                        {section.visible ? (
                          <VisibilityIcon fontSize="small" />
                        ) : (
                          <VisibilityOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    {section.visible && (
                      <>
                        {section.editable && section.content !== null ? (
                          <TextField
                            multiline
                            minRows={2}
                            maxRows={8}
                            fullWidth
                            value={section.content || ''}
                            onChange={(e) =>
                              handleContentChange(section.id, e.target.value)
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.88rem',
                                bgcolor: 'rgba(255,255,255,0.5)',
                              },
                            }}
                          />
                        ) : section.data ? (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 3,
                              flexWrap: 'wrap',
                              py: 1,
                            }}
                          >
                            {Object.entries(section.data).map(([key, value]) => (
                              <Box key={key} sx={{ textAlign: 'center', minWidth: 80 }}>
                                <Typography
                                  sx={{
                                    fontFamily: '"Playfair Display", Georgia, serif',
                                    fontWeight: 600,
                                    fontSize: '1.4rem',
                                    color: colors.primary,
                                  }}
                                >
                                  {typeof value === 'number' && key === 'nss'
                                    ? `${value > 0 ? '+' : ''}${value.toFixed(1)}`
                                    : value}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '0.72rem',
                                    color: colors.textMed,
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {key.replace(/_/g, ' ')}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: '"DM Sans", sans-serif',
                              fontSize: '0.85rem',
                              color: colors.textLight,
                              fontStyle: 'italic',
                            }}
                          >
                            Aucune donnee disponible.
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                ))}
            </Box>

            {/* Comments field */}
            <Divider sx={{ my: 3, borderColor: colors.borderLight }} />
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 500,
                fontSize: '1rem',
                color: colors.charcoal,
                mb: 1,
              }}
            >
              Notes du consultant (optionnel)
            </Typography>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Ajoutez vos observations, recommandations personnalisees, ou commentaires pour le client..."
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.88rem',
                  bgcolor: 'rgba(255,255,255,0.5)',
                },
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: colors.ivory }}>
        <Typography
          sx={{
            flex: 1,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.78rem',
            color: colors.textLight,
          }}
        >
          {visibleCount}/{sections.length} sections incluses
        </Typography>

        <Button
          onClick={handleClose}
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 600,
            color: colors.textMed,
          }}
        >
          Fermer
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={generating || loading || visibleCount === 0}
          startIcon={
            generating ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            bgcolor: colors.primary,
            borderRadius: '100px',
            textTransform: 'none',
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 600,
            px: 3,
            '&:hover': { bgcolor: colors.primaryDark },
          }}
        >
          {generating ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReportEditor;
